import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import type { ModuleRoute } from '@core/router/types';
import { Permissions } from '@core/auth/permissions';

// Lazy-loaded page components for code splitting
const FleetDashboardPage = lazy(() => import('./pages/FleetDashboardPage.tsx'));
const VehicleListPage = lazy(() => import('./pages/vehicles/VehicleListPage.tsx'));
const VehicleDetailPage = lazy(() => import('./pages/vehicles/VehicleDetailPage.tsx'));
const VehicleFormPage = lazy(() => import('./pages/vehicles/VehicleFormPage.tsx'));
const ServiceListPage = lazy(() => import('./pages/services/ServiceListPage.tsx'));
const ServiceDetailPage = lazy(() => import('./pages/services/ServiceDetailPage.tsx'));
const ServiceFormPage = lazy(() => import('./pages/services/ServiceFormPage.tsx'));
const ContractListPage = lazy(() => import('./pages/contracts/ContractListPage.tsx'));
const ContractDetailPage = lazy(() => import('./pages/contracts/ContractDetailPage.tsx'));
const ContractFormPage = lazy(() => import('./pages/contracts/ContractFormPage.tsx'));
const ProviderListPage = lazy(() => import('./pages/providers/ProviderListPage.tsx'));
const ProviderFormPage = lazy(() => import('./pages/providers/ProviderFormPage.tsx'));
const ProviderDetailPage = lazy(() => import('./pages/providers/ProviderDetailPage.tsx'));
const RegistrationListPage = lazy(() => import('./pages/registrations/RegistrationListPage.tsx'));
const RegistrationDetailPage = lazy(() => import('./pages/registrations/RegistrationDetailPage.tsx'));
const RegistrationFormPage = lazy(() => import('./pages/registrations/RegistrationFormPage.tsx'));
const TireProcurementListPage = lazy(() => import('./pages/tire-procurement/TireProcurementListPage.tsx'));
const TireServiceDetailPage = lazy(() => import('./pages/tire-procurement/TireServiceDetailPage.tsx'));
const VehicleRequestListPage = lazy(() => import('./pages/vehicle-requests/VehicleRequestListPage.tsx'));
const VehicleRequestDetailPage = lazy(() => import('./pages/vehicle-requests/VehicleRequestDetailPage.tsx'));
const VehicleRequestFormPage = lazy(() => import('./pages/vehicle-requests/VehicleRequestFormPage.tsx'));
const TravelOrderListPage = lazy(() => import('./pages/travel-orders/TravelOrderListPage.tsx'));
const TravelOrderDetailsPage = lazy(() => import('./pages/travel-orders/TravelOrderDetailsPage.tsx'));
const TravelOrderFormPage = lazy(() => import('./pages/travel-orders/TravelOrderFormPage.tsx'));
const TravelOrderPeriodListPage = lazy(() => import('./pages/travel-order-periods/TravelOrderPeriodListPage.tsx'));
const TravelOrderPeriodDetailPage = lazy(() => import('./pages/travel-order-periods/TravelOrderPeriodDetailPage.tsx'));
const FuelLogListPage = lazy(() => import('./pages/fuel-logs/FuelLogListPage.tsx'));
const FuelLogDetailPage = lazy(() => import('./pages/fuel-logs/FuelLogDetailPage.tsx'));
const TireListPage = lazy(() => import('./pages/tires/TireListPage.tsx'));
const FleetUserListPage = lazy(() => import('./pages/fleet-users/FleetUserListPage.tsx'));
const FleetUserDetailPage = lazy(() => import('./pages/fleet-users/FleetUserDetailPage.tsx'));
const FleetUserFormPage = lazy(() => import('./pages/fleet-users/FleetUserFormPage.tsx'));
const UserAccountPage = lazy(() => import('../users/pages/UserAccountPage'));

/**
 * Fleet Module Routes
 *
 * All routes are relative to /fleets base path.
 * Routes with permission prop require that permission to access.
 */
export const fleetRoutes: ModuleRoute[] = [
  // Default redirect to vozila
  {
    index: true,
    element: <Navigate to="vozila" replace />,
  },

  // Dashboard
  {
    path: 'dashboard',
    element: <FleetDashboardPage />,
    permission: Permissions.Fleet.Vehicles.Read,
    meta: {
      title: 'Fleet Dashboard',
      showInSidebar: false,
    },
  },

  // Vehicles (Vozila)
  {
    path: 'vozila',
    element: <VehicleListPage />,
    permission: Permissions.Fleet.Vehicles.Read,
    meta: {
      title: 'Vozila',
      icon: 'DirectionsCar',
      showInSidebar: true,
      order: 1,
    },
  },
  {
    path: 'vozila/novi',
    element: <VehicleFormPage />,
    permission: Permissions.Fleet.Vehicles.Create,
    meta: {
      title: 'Novo vozilo',
      showInSidebar: false,
    },
  },
  {
    path: 'vozila/:id',
    element: <VehicleDetailPage />,
    permission: Permissions.Fleet.Vehicles.Read,
    meta: {
      title: 'Detalji vozila',
      showInSidebar: false,
    },
  },
  {
    path: 'vozila/:id/izmeni',
    element: <VehicleFormPage />,
    permission: Permissions.Fleet.Vehicles.Update,
    meta: {
      title: 'Izmeni vozilo',
      showInSidebar: false,
    },
  },
  {
    path: 'vozila/zahtevi',
    element: <VehicleRequestListPage />,
    permission: Permissions.Fleet.Requests.Read,
    meta: {
      title: 'Zahtjevi za vozila',
      showInSidebar: false,
    },
  },
  {
    path: 'vozila/zahtevi/novi',
    element: <VehicleRequestFormPage />,
    permission: Permissions.Fleet.Requests.Create,
    meta: {
      title: 'Novi zahtjev',
      showInSidebar: false,
    },
  },
  {
    path: 'vozila/zahtevi/:id',
    element: <VehicleRequestDetailPage />,
    permission: Permissions.Fleet.Requests.Read,
    meta: {
      title: 'Detalji zahtjeva',
      showInSidebar: false,
    },
  },

  // Vehicle Requests (Zahtjevi za vozila) - Keep old routes for backward compatibility
  {
    path: 'zahtevi',
    element: <VehicleRequestListPage />,
    permission: Permissions.Fleet.Requests.Read,
    meta: {
      title: 'Zahtjevi za vozila',
      icon: 'Assignment',
      showInSidebar: true,
      order: 7,
    },
  },
  {
    path: 'zahtevi/novi',
    element: <VehicleRequestFormPage />,
    permission: Permissions.Fleet.Requests.Create,
    meta: {
      title: 'Novi zahtjev',
      showInSidebar: false,
    },
  },
  {
    path: 'zahtevi/:id',
    element: <VehicleRequestDetailPage />,
    permission: Permissions.Fleet.Requests.Read,
    meta: {
      title: 'Detalji zahtjeva',
      showInSidebar: false,
    },
  },

  // Vožnje (Trips/Travel Orders)
  // Unified page with tabs for "My Trips" and "All Trips"
  {
    path: 'voznje',
    element: <TravelOrderListPage />,
    permission: Permissions.Fleet.TravelOrders.Read,
    meta: {
      title: 'Vožnje',
      icon: 'Assignment',
      showInSidebar: true,
      order: 8,
    },
  },
  {
    path: 'voznje/novi',
    element: <TravelOrderFormPage />,
    permission: Permissions.Fleet.TravelOrders.Manage,
    meta: {
      title: 'Nova vožnja',
      showInSidebar: false,
    },
  },
  {
    path: 'voznje/:id',
    element: <TravelOrderDetailsPage />,
    permission: Permissions.Fleet.TravelOrders.Read,
    meta: {
      title: 'Detalji vožnje',
      showInSidebar: false,
    },
  },
  {
    path: 'voznje/:id/izmeni',
    element: <TravelOrderFormPage />,
    permission: Permissions.Fleet.TravelOrders.Manage,
    meta: {
      title: 'Izmeni vožnju',
      showInSidebar: false,
    },
  },

  // Putni nalozi (Travel Order Periods)
  // List page - shows in sidebar
  {
    path: 'putni-nalozi',
    element: <TravelOrderPeriodListPage />,
    requireAuth: true, // Skip permission check for now
    meta: {
      title: 'Putni nalozi',
      icon: 'CalendarMonth',
      showInSidebar: true,
      order: 9,
    },
  },
  // Detail page - doesn't show in sidebar
  {
    path: 'putni-nalozi/:id',
    element: <TravelOrderPeriodDetailPage />,
    requireAuth: true, // Skip permission check for now
    meta: {
      title: 'Detalji putnog naloga',
      showInSidebar: false,
    },
  },

  // Services (Servisi) - Maps to Operations
  {
    path: 'servisi',
    element: <ServiceListPage />,
    permission: Permissions.Fleet.Operations.Read,
    meta: {
      title: 'Servisi',
      icon: 'Build',
      showInSidebar: true,
      order: 2,
    },
  },
  {
    path: 'servisi/novi',
    element: <ServiceFormPage />,
    permission: Permissions.Fleet.Operations.MaintenanceManage,
    meta: {
      title: 'Novi servis',
      showInSidebar: false,
    },
  },
  {
    path: 'servisi/:id',
    element: <ServiceDetailPage />,
    permission: Permissions.Fleet.Operations.Read,
    meta: {
      title: 'Detalji servisa',
      showInSidebar: false,
    },
  },
  {
    path: 'servisi/:id/izmeni',
    element: <ServiceFormPage />,
    permission: Permissions.Fleet.Operations.MaintenanceManage,
    meta: {
      title: 'Izmeni servis',
      showInSidebar: false,
    },
  },

  // Fuel Logs (Gorivo) - Maps to Operations
  {
    path: 'gorivo',
    element: <FuelLogListPage />,
    permission: Permissions.Fleet.Operations.Read,
    meta: {
      title: 'Gorivo',
      icon: 'LocalGasStation',
      showInSidebar: true,
      order: 3,
    },
  },
  {
    path: 'gorivo/:id',
    element: <FuelLogDetailPage />,
    permission: Permissions.Fleet.Operations.Read,
    meta: {
      title: 'Detalji unosa goriva',
      showInSidebar: false,
    },
  },

  // Providers (Dobavljači)
  {
    path: 'dobavljaci',
    element: <ProviderListPage />,
    permission: Permissions.Fleet.Providers.Read,
    meta: {
      title: 'Dobavljači',
      icon: 'Business',
      showInSidebar: true,
      order: 3,
    },
  },
  {
    path: 'dobavljaci/novi',
    element: <ProviderFormPage />,
    permission: Permissions.Fleet.Providers.Manage,
    meta: {
      title: 'Novi dobavljač',
      showInSidebar: false,
    },
  },
  {
    path: 'dobavljaci/:id',
    element: <ProviderDetailPage />,
    permission: Permissions.Fleet.Providers.Read,
    meta: {
      title: 'Detalji dobavljača',
      showInSidebar: false,
    },
  },
  {
    path: 'dobavljaci/:id/izmeni',
    element: <ProviderFormPage />,
    permission: Permissions.Fleet.Providers.Manage,
    meta: {
      title: 'Izmeni dobavljača',
      showInSidebar: false,
    },
  },
  {
    path: 'dobavljaci/:providerId/ugovori/novi',
    element: <ContractFormPage />,
    permission: Permissions.Fleet.Contracts.Manage,
    meta: {
      title: 'Novi ugovor',
      showInSidebar: false,
    },
  },

  // Registrations (Registracije)
  {
    path: 'registracije',
    element: <RegistrationListPage />,
    permission: Permissions.Fleet.Operations.Read,
    meta: {
      title: 'fleets.registrations.title',
      icon: 'Article',
      showInSidebar: true,
      order: 6,
    },
  },
  {
    path: 'registracije/novi',
    element: <RegistrationFormPage />,
    permission: Permissions.Fleet.Operations.RegistrationManage,
    meta: {
      title: 'fleets.registrations.createRegistration',
      showInSidebar: false,
    },
  },
  {
    path: 'registracije/:id',
    element: <RegistrationDetailPage />,
    permission: Permissions.Fleet.Operations.Read,
    meta: {
      title: 'fleets.registrations.title',
      showInSidebar: false,
    },
  },
  {
    path: 'registracije/:id/izmeni',
    element: <RegistrationFormPage />,
    permission: Permissions.Fleet.Operations.RegistrationManage,
    meta: {
      title: 'fleets.registrations.editRegistration',
      showInSidebar: false,
    },
  },

  // Contracts (Ugovori)
  {
    path: 'ugovori',
    element: <ContractListPage />,
    permission: Permissions.Fleet.Contracts.Read,
    meta: {
      title: 'Ugovori',
      icon: 'Description',
      showInSidebar: true,
      order: 4,
    },
  },
  {
    path: 'ugovori/novi',
    element: <ContractFormPage />,
    permission: Permissions.Fleet.Contracts.Manage,
    meta: {
      title: 'Novi ugovor',
      showInSidebar: false,
    },
  },
  {
    path: 'ugovori/:id',
    element: <ContractDetailPage />,
    permission: Permissions.Fleet.Contracts.Read,
    meta: {
      title: 'Detalji ugovora',
      showInSidebar: false,
    },
  },
  {
    path: 'dobavljaci/:providerId/ugovori/:contractId/izmeni',
    element: <ContractFormPage />,
    permission: Permissions.Fleet.Contracts.Manage,
    meta: {
      title: 'Izmeni ugovor',
      showInSidebar: false,
    },
  },

  // Tires (Gume) - Tire tracking (new aggregate)
  {
    path: 'gume',
    element: <TireListPage />,
    permission: Permissions.Fleet.Tires.Read,
    meta: {
      title: 'Evidencija guma',
      showInSidebar: false,
    },
  },

  // Tire Procurement (Nabavka guma) - Maps to Operations
  {
    path: 'nabavka-guma',
    element: <TireProcurementListPage />,
    permission: Permissions.Fleet.Operations.Read,
    meta: {
      title: 'Nabavka guma',
      icon: 'LocalShipping',
      showInSidebar: true,
      order: 5,
    },
  },
  {
    path: 'nabavka-guma/:id',
    element: <TireServiceDetailPage />,
    permission: Permissions.Fleet.Operations.Read,
    meta: {
      title: 'Detalji servisa guma',
      showInSidebar: false,
    },
  },

  // Fleet Users (Korisnici)
  {
    path: 'korisnici',
    element: <FleetUserListPage />,
    permission: Permissions.Fleet.FleetUsers.Read,
    meta: {
      title: 'Korisnici',
      icon: 'People',
      showInSidebar: true,
      order: 9,
    },
  },
  {
    path: 'korisnici/novi',
    element: <Navigate to="/korisnici/novi" replace />,
    meta: {
      title: 'Novi korisnik',
      showInSidebar: false,
    },
  },
  {
    path: 'korisnici/:id',
    element: <FleetUserDetailPage />,
    permission: Permissions.Fleet.FleetUsers.Read,
    meta: {
      title: 'Detalji korisnika',
      showInSidebar: false,
    },
  },
  {
    path: 'korisnici/:id/izmeni',
    element: <FleetUserFormPage />,
    permission: Permissions.Fleet.FleetUsers.Update,
    meta: {
      title: 'Izmeni korisnika',
      showInSidebar: false,
    },
  },

  // User Account (Moj nalog) - All authenticated users
  {
    path: 'profil',
    element: <UserAccountPage />,
    requireAuth: true,
    meta: {
      title: 'Moj nalog',
      icon: 'AccountCircle',
      showInSidebar: true,
      order: 99,
    },
  },
];
